import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SortDescriptor } from '@progress/kendo-data-query';
import { CustomHttpHeaders } from 'cl-common-angular';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CampaignStatus } from '../constants/lookups';
import { Campaign, CampaignOpen, CampaignPageData, CampaignStats, ICampaignState } from './campaign';
import { CampaignDashboard } from './campaign-dashboard';
import { CampaignGeneralSection } from './campaign-general-section';
import { CampaignInformation } from './campaign-information';
import { CampaignLimits } from './campaign-limits';
import { CampaignSocialMediaShare } from './campaign-social-media-share';
import { CampaignTaskList } from './campaign-task-list';
import { CampaignTrendMetrics } from './campaign-trend-metrics';
import { CampaignsFilter } from './campaigns-filter';
import { CurrentCampaignStatus } from './current-campaign-status';
import { NewStudentOnboardingInfo } from './new-student-onboarding-info';
import { StudentCampaign } from './student-campaign';

@Injectable({
    providedIn: 'root'
})
export class CampaignService {

    private readonly routePrefix: string = '/api/campaign/';
    private readonly autofillParamKey: string = 'autofill';
    private refresh = new BehaviorSubject<void>(undefined);

    public defaultState = {
        state: {
            skip: 0,
            take: 10,
            sort: [{
            } as SortDescriptor],
            filter: {
                logic: 'and',
                filters: []
            },
        },
        sortState: {
            sortByDefault: 'Alphabetical',
            sortByOptions: ['Alphabetical', 'Percent Raised', 'End Date'],
            sortByDir: 'Ascending',
            sortByDirOptions: ['Ascending', 'Descending']
        }
    } as ICampaignState;

    campaignsFilters$ = new BehaviorSubject<CampaignsFilter>({} as CampaignsFilter);
    campaignsStatus$ = new BehaviorSubject<CampaignStatus>(CampaignStatus.Draft);
    campaignsState$ = new BehaviorSubject<ICampaignState>(this.defaultState);
    campaignStats$ = new BehaviorSubject<CampaignStats | null>(null);
    campaignCompleteEdit$ = new BehaviorSubject<CampaignPageData | null>(null);
    _limits = new Map<number, Subject<CampaignLimits>>();
    campaignForEdit$ = new BehaviorSubject<NewStudentOnboardingInfo | null>(null);

    currentCampaigns$ = combineLatest([this.campaignsFilters$, this.campaignsStatus$, this.campaignsState$]).pipe(
        switchMap(([filter, status, state]) => {
            return this.getCampaigns$(filter, status, state);
        })
    );

    taskList$ = this.refresh.pipe(
        switchMap(() => this.http.get<CampaignTaskList>(`${this.routePrefix}tasks`,
            { headers: new HttpHeaders({ [CustomHttpHeaders.disableSpinner]: 'Y' }) })
        )
    );

    refreshTaskListData(): void {
        this.refresh.next();
    }

    constructor(private http: HttpClient, private activatedRoute: ActivatedRoute) { }

    getCampaignsFilter = () => this.campaignsFilters$.getValue();
    setCampaignsFilter = (filter: CampaignsFilter) => this.campaignsFilters$.next(filter);
    getCampaignsStatus = () => this.campaignsStatus$.getValue();
    setCampaignsStatus = (status: CampaignStatus) => this.campaignsStatus$.next(status);
    getCampaignsState = () => this.campaignsState$.getValue();
    setCampaignsState = (state: ICampaignState) => this.campaignsState$.next(state);

    getTotalCampaignsFilterApplied(filters: CampaignsFilter) {
        return ((filters.studentId != null && filters.studentId.length > 0 ? 1 : 0)
            + (filters.firstName != null && filters.firstName.length > 0 ? 1 : 0)
            + (filters.lastName != null && filters.lastName.length > 0 ? 1 : 0)
            + (filters.goalAmountMin != null ? 1 : 0)
            + (filters.goalAmountMax != null ? 1 : 0)
            + (filters.dollarsRaisedMin != null ? 1 : 0)
            + (filters.dollarsRaisedMax != null ? 1 : 0)
            + (filters.campaignStartDateOption != null ? 1 : 0)
            + (filters.campaignStartCustomStartDate != null ? 1 : 0)
            + (filters.campaignStartCustomEndDate != null ? 1 : 0)
            + (filters.campaignEndDateOption != null ? 1 : 0)
            + (filters.campaignEndCustomStartDate != null ? 1 : 0)
            + (filters.campaignEndCustomEndDate != null ? 1 : 0));
    }

    getCampaigns$ = (filter: CampaignsFilter, status: CampaignStatus, input: ICampaignState) => {
        // clears the status popup as the user navigates tabs
        this.campaignStats$.next(null);

        return this.http.post<Campaign>(`${this.routePrefix}`, {
            campaignFilter: filter,
            statusId: status.toString(),
            skip: input.state.skip,
            take: input.state.take,
            sort: input.state.sort ? input.state.sort[0] : null,
            sortByDefault: input.sortState.sortByDefault,
            sortByDir: input.sortState.sortByDir
        });
    }

    getCampaignsCsv = (filter: CampaignsFilter, status: CampaignStatus, input: ICampaignState) => this.http.post<Blob>(`${this.routePrefix}csv`, {
        campaignFilter: filter,
        statusId: status.toString(),
        skip: input.state.skip ? input.state.skip : null,
        take: null,
        sort: input.state.sort ? input.state.sort[0] : null,
        sortByDefault: input.sortState.sortByDefault,
        sortByDir: input.sortState.sortByDir
    }, { responseType: 'text' as any })

    getCampaignTaskList = () => this.http.get<CampaignTaskList>(`${this.routePrefix}tasks`);

    getCampaignInformation = (id: number) => this.http.get<CampaignInformation>(`${this.routePrefix}${id}`);
    getCampaignForEdit = (id: number, verifiedStudentId: string) => this.http.get<NewStudentOnboardingInfo>(`${this.routePrefix}edit/${id}/${verifiedStudentId}`).pipe(
        switchMap((response) => {
            this.campaignForEdit$.next(response);
            return this.campaignForEdit$;
        })
    ).pipe(map(data => Object.assign(new NewStudentOnboardingInfo(), data)))

    getCampaignStatus = () =>
        this.http.get<{ showWelcomePage: boolean, showOnboardingPage: boolean, isNewUser: boolean }>(
            `${this.routePrefix}status`,
            { headers: new HttpHeaders({ [CustomHttpHeaders.disableSpinner]: 'Y' }) }
        )

    getCurrentCampaignStatus = () =>
        this.http.get<CurrentCampaignStatus>(
            `${this.routePrefix}currentstatus`,
            { headers: new HttpHeaders({ [CustomHttpHeaders.disableSpinner]: 'Y' }) }
        )

    getCampaignDashboard = () => this.http.get<CampaignDashboard>(`${this.routePrefix}dashboard`).pipe(
        map(res => {
            res.goalPercentage = (100 * res.raisedAmount) / res.goalAmount || 0;
            return res;
        })
    )

    getCampaignTrendMetrics = () => this.http.get<CampaignTrendMetrics>(`${this.routePrefix}metrics`);

    getCampaignSocialMediaShare = (socialMediaTypeItemId: string) => this.http.get<CampaignSocialMediaShare>(`${this.routePrefix}campaignshare`,
        { params: new HttpParams().set('socialMediaTypeItemId', socialMediaTypeItemId) })

    getNewStudentOnboardingInfo = () => this.http.get<NewStudentOnboardingInfo>(`${this.routePrefix}onboarding`,
        {
            params: new HttpParams().set('autofill',
                this.activatedRoute.snapshot.queryParams[this.autofillParamKey]
                    ? this.activatedRoute.snapshot.queryParams[this.autofillParamKey].toString()
                    : '')
        }).pipe(map(data => Object.assign(new NewStudentOnboardingInfo(), data)))

    saveOnboardingStepOne = (onboardingInfo: NewStudentOnboardingInfo) => {
        const postBody = {
            campaignId: onboardingInfo.campaignId,
            emailAddress: onboardingInfo.emailAddress,
            mobilePhone: onboardingInfo.mobilePhone,
            yearInSchoolId: onboardingInfo.yearInSchoolId,
            clientMajorId: onboardingInfo.clientMajorId,
            termsAccepted: onboardingInfo.termsAccepted
        };
        return this.http.post(
            `${this.routePrefix}onboarding/stepone`, postBody, { headers: new HttpHeaders({ [CustomHttpHeaders.disableSpinner]: 'Y' }) }
        );
    }

    saveOnboardingStepTwo = (onboardingInfo: NewStudentOnboardingInfo) => {
        const postBody = {
            campaignId: onboardingInfo.campaignId,
            layoutTemplateId: onboardingInfo.layoutTemplateId
        };
        return this.http.post(
            `${this.routePrefix}onboarding/steptwo`, postBody, { headers: new HttpHeaders({ [CustomHttpHeaders.disableSpinner]: 'Y' }) }
        );
    }

    saveOnboardingStepThree = (onboardingInfo: NewStudentOnboardingInfo) => {
        const postBody = {
            campaignId: onboardingInfo.campaignId,
            goalAmount: onboardingInfo.goalAmount
        };
        return this.http.post(
            `${this.routePrefix}onboarding/stepthree`, postBody, { headers: new HttpHeaders({ [CustomHttpHeaders.disableSpinner]: 'Y' }) }
        );
    }

    saveOnboardingStepFour = (onboardingInfo: NewStudentOnboardingInfo) => {
        const postBody = {
            campaignId: onboardingInfo.campaignId,
            storySections: onboardingInfo.storySections,
            verifiedStudentId: onboardingInfo.verifiedStudentId
        };
        return this.http.post(
            `${this.routePrefix}onboarding/stepfour`, postBody, { headers: new HttpHeaders({ [CustomHttpHeaders.disableSpinner]: 'Y' }) }
        );
    }
    saveGeneralSection = (section: CampaignGeneralSection) =>
        this.http.post(`${this.routePrefix}general`, section)

    launchCampaign = () => this.http.post<boolean>(`${this.routePrefix}onboarding/launch`, null);

    getCampaignLimits(id: number, refresh = false): Observable<CampaignLimits> {
        let limits = this._limits.get(id);

        if (!limits) {
            limits = new Subject<CampaignLimits>();
            this._limits.set(id, limits);

            refresh = true;
        }

        if (refresh) {
            const s = this.getCampaignLimitsServerRequest(id).subscribe(d => {
                limits?.next(d);
            }, undefined, () => { s.unsubscribe(); });
        }

        return limits.asObservable();
    }

    updateCampaignLimits(id: number, data: CampaignLimits) {
        const limits = this._limits.get(id);

        // Our design currently requires a call to getCampaignLimits() first, before the client
        // is allowed to update.
        if (!limits) {
            throw new Error(`Failed to lookup limits for campaign ${id}`);
        }

        limits.next(data);
    }

    private getCampaignLimitsServerRequest = (id: number) => this.http.get<CampaignLimits>(`${this.routePrefix}${id}/limits`);

    getProfileStudentCampaigns = (userId: string | null) => this.http.get<StudentCampaign[]>(`${this.routePrefix}profile/${userId}`);
    getStudentCampaigns = () => this.http.get<StudentCampaign[]>(`${this.routePrefix}student`);
    removeCampaign = (campaignId: number, verifiedStudentId: string) =>
        this.http.delete(`${this.routePrefix}${campaignId}/${verifiedStudentId}`)

    openCampaign = (data: CampaignOpen) => this.http.post<NewStudentOnboardingInfo>(`${this.routePrefix}open`, data).pipe(
        switchMap((response) => {
            this.campaignForEdit$.next(response);
            return of(response);
        })
    )

    changeCampaignStatus = (campaignId: number, verifiedStudentId: string, status: string) =>
        this.http.post<NewStudentOnboardingInfo>(`${this.routePrefix}changestatus/${campaignId}/${verifiedStudentId}`, { status }).pipe(
            switchMap((response) => {
                this.campaignForEdit$.next(response);
                return of(response);
            })
        )

    changeStudentCampaignStatus = (campaignId: number, status: string) =>
        this.http.post(`${this.routePrefix}changestatus/${campaignId}`, { status })

    createStudentInfoRecord = () => this.http.post(`${this.routePrefix}createstudentinfo`, {});

}
